import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { useNavigate, useParams } from 'react-router-dom';

import { apiPaths } from '../../../../service/apiPaths';
import ApiService from '../../../../service/ApiService';
import { formatDate, leadStyes } from '../../../../util/constant';

interface Message {
  _id: string;
  message: string;
  sender?: string;
  createdAt: string;
  link?: string;
  userId?: {
    _id: string;
    name: string;
    role: string;
    subRole: string;
  };
  success?: string;
}

const LeadDetail: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [lead, setLead] = React.useState<any | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [messages, setMessages] = React.useState<Message[]>([]);
  const [newMessage, setNewMessage] = React.useState("");
  const [selectedImage, setSelectedImage] = React.useState<File | null>(null);
  const [imagePreview, setImagePreview] = React.useState<string | null>(null);
  const [editMessage, setEditMessage ] = React.useState<Message | null>(null)
  const [editingMessageId, setEditingMessageId] = React.useState<string | null>(null)
  const [editMessageText, setEditMessageText] = React.useState("")
  const messagesEndRef = React.useRef<HTMLDivElement>(null);
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const [deleteMessage, setDeleteMessage] = useState<string| null>(null)

  React.useEffect(() => {
    const fetchLeadDetails = async () => {
      try {
        const response = await ApiService({
          method: "GET",
          endpoint: `${apiPaths.getAllLeads}/${id}`,
        });
        setLead(response);
      } catch (error) {
        console.error("Error fetching lead details:", error);
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchLeadDetails();
      fetchMessages();
    }
  }, [id]);

  const fetchMessages = async () => {
    try {
      const response = await ApiService({
        method: "GET",
        endpoint: apiPaths.messages.replace(":id", id || ""),
      });

      setMessages(response.messages);
      console.log("messages", messages);
      
    } catch (error) {
      console.error("Error fetching messages:", error);
    }
  };

  const handleEditMessage = async (messageIndex: number) => {
    try {
      const response = await ApiService({
        method: "GET",
        endpoint: apiPaths.editMessage.replace(":id", id || "").replace(":messageIndex", messageIndex.toString())
      })
      
      setEditMessage(response.message)
      setEditingMessageId(response.message._id)
      setEditMessageText(response.message.message)
    } catch (error) {
      console.error("Error fetching message:", error);
      toast.error("Error fetching message for editing");
    }
  }

  const handleDeleteMessage = async (messageIndex: number) => {
    try {
      const response = await ApiService({
        method: "DELETE",
        endpoint: apiPaths.deleteMessage.replace(":id", id || '').replace(":messageIndex", messageIndex.toString())
      });

      // Remove the message from the local state
      setMessages(prevMessages => prevMessages.filter((_, idx) => idx !== messageIndex));
      toast.success(response.success);
    } catch (error) {
      console.error("Error deleting message:", error);
      toast.error("Error deleting message");
    }
  };

  const handleUpdateMessage = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (!editingMessageId || !editMessageText.trim()) return;

    try {
      await ApiService({
        method: "PUT",
        endpoint: apiPaths.editMessage.replace(":id", id || "").replace(":messageIndex", messages.findIndex(m => m._id === editingMessageId).toString()),
        data: {
          message: editMessageText
        }
      });

      // Update the message in the local state
      setMessages(prevMessages => 
        prevMessages.map(msg => 
          msg._id === editingMessageId 
            ? { ...msg, message: editMessageText }
            : msg
        )
      );

      setEditingMessageId(null);
      setEditMessageText("");
      setEditMessage(null);
      toast.success("Message updated successfully");
    } catch (error: any) {
      console.error("Error updating message:", error);

      toast.error(error?.response?.data?.message || "You can only edit your own messages");
    }
  }

  const handleCancelEdit = () => {
    setEditingMessageId(null);
    setEditMessageText("");
    setEditMessage(null);
  }

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  React.useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleStatusChange = async (newStatus: string) => {
    try {
      await ApiService({
        method: "PUT",
        endpoint: `${apiPaths.leadStatus}${lead._id}`,
        data: {
          leadStatus: newStatus,
        },
      });
      setLead((prev: any) => ({ ...prev, leadStatus: newStatus }));
      toast.success("Lead status updated successfully");
    } catch (error: any) {
      toast.error(
        error?.response?.data?.message || "Error updating lead status"
      );
    }
  };

  const handleImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedImage(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveImage = () => {
    setSelectedImage(null);
    setImagePreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newMessage.trim() && !selectedImage) return;

    try {
      let imageLink = null;

      // First upload the image if one is selected
      if (selectedImage) {
        const imageFormData = new FormData();
        imageFormData.append("image", selectedImage);

        const uploadResponse = await ApiService({
          method: "POST",
          endpoint: apiPaths.upload_image, // Make sure this endpoint is defined in apiPaths
          data: imageFormData,
          headers: {
            "Content-Type": "multipart/form-data",
          },
        });

        imageLink = uploadResponse.imageUrl; // Assuming the response contains a 'link' field
      }

      // Then send the message with the image link
      await ApiService({
        method: "POST",
        endpoint: apiPaths.messages.replace(":id", id || ""),
        data: {
          message: newMessage,
          link: imageLink, // Include the image link in the message data
        },
      });

      setNewMessage("");
      setSelectedImage(null);
      setImagePreview(null);
      fetchMessages();
      toast.success("Message sent successfully");
    } catch (error: any) {
      toast.error(error?.response?.data?.message || "Error sending message");
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (!lead) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="text-red-500 text-xl font-semibold">Lead not found</div>
      </div>
    );
  }

  return (
    <div className="max-h-[84vh] bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-6">
          <button
            onClick={() => navigate(-1)}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-blue-700 bg-blue-100 hover:bg-blue-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            <svg
              className="mr-2 h-5 w-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M10 19l-7-7m0 0l7-7m-7 7h18"
              />
            </svg>
            Back
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {/* Left Column - Lead Details */}
          <div className="bg-white rounded-xl shadow-lg overflow-auto h-[calc(100vh-12rem)]">
            <div className="px-6 py-4 bg-gradient-to-r from-blue-500 to-blue-600">
              <h1 className="text-2xl font-bold text-white">Lead Details</h1>
            </div>

            <div className="p-6">
              <div className="space-y-6">
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="space-y-4">
                    <div>
                      <p className="mt-1 text-lg text-gray-900 font-bold">
                        {lead.name} ( {lead.email})
                      </p>
                    </div>
                    <div>
                      <h3 className="text-sm font-medium text-gray-500">
                        Description
                      </h3>
                      <textarea
                        value={lead.description || "-"}
                        className="mt-1 w-full text-gray-900 whitespace-pre-wrap bg-white p-3 rounded-md border border-gray-200"
                        disabled
                      />
                    </div>
                    <div className="flex justify-between">
                      <div>
                        <h3 className="text-sm font-medium text-gray-500">
                          Company Name
                        </h3>
                        <p className="mt-1 text-lg text-gray-900">
                          {lead.companyName || "-"}
                        </p>
                      </div>
                      <div>
                        {lead.linkedin && (
                          <div>
                            <h3 className="text-sm font-medium text-gray-500 flex justify-end">
                              Lead Web Link
                            </h3>
                            <a
                              href={lead.linkedin}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="mt-1 text-blue-600 hover:text-blue-800"
                            >
                              View LeadId
                            </a>
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="flex justify-between">
                      <div>
                        <h3 className="text-sm font-medium text-gray-500">
                          Phone
                        </h3>
                        <p className="mt-1 text-lg text-gray-900">
                          {lead.phone || "-"}
                        </p>
                      </div>
                      <div>
                        <h3 className="text-sm font-medium text-gray-500 flex justify-end">
                          Status
                        </h3>
                        <select
                          value={lead.leadStatus}
                          onChange={(e) => handleStatusChange(e.target.value)}
                          className={`mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md ${
                            leadStyes[lead.leadStatus]
                          }`}
                        >
                          <option value="New">New</option>
                          <option value="Send For Review">
                            Send For Review
                          </option>
                          <option value="Approved">Approved</option>
                          <option value="Rejected">Rejected</option>
                          <option value="Contacted">Contacted</option>
                          <option value="Converted">Converted</option>
                          <option value="Lost">Lost</option>
                          <option value="Duplicate">Duplicate</option>
                          <option value="Not Interested">Not Interested</option>
                          <option value="On Hold">On Hold</option>
                          <option value="Reminder">Reminder</option>
                        </select>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-gray-50 rounded-lg p-4">
                  <h2 className="text-lg font-semibold text-gray-900 mb-4">
                    Additional Details
                  </h2>

                  <div className="space-y-4">
                    {/* <div>
                      <h3 className="text-sm font-medium text-gray-500">
                        Description
                      </h3>
                      <p className="mt-1 text-gray-900 whitespace-pre-wrap bg-white p-3 rounded-md border border-gray-200">
                        {lead.description}
                      </p>
                    </div> */}

                    <div className="flex justify-between">
                      <div>
                        <h3 className="text-sm font-medium text-gray-500">
                          Platform
                        </h3>
                        <p className="mt-1 text-gray-900">
                          {lead.platform?.name || "-"}
                        </p>
                      </div>
                      <div>
                        <h3 className="text-sm font-medium text-gray-500 flex justify-end">
                          Services
                        </h3>
                        <p className="mt-1 text-gray-900 flex justify-end">
                          {lead.services?.type || "-"}
                        </p>
                      </div>
                    </div>

                    <div className="flex justify-between">
                      <div>
                        <h3 className="text-sm font-medium text-gray-500">
                          Interest
                        </h3>
                        <p className="mt-1 text-gray-900">
                          {lead.interest?.type || "-"}
                        </p>
                      </div>
                      <div>
                        <h3 className="text-sm font-medium text-gray-500 flex justify-end">
                          Created At
                        </h3>
                        <p className="mt-1 text-gray-900">
                          {formatDate(lead.createdAt) || "-"}
                        </p>
                      </div>
                    </div>

                    <div className="flex justify-between">
                      <div>
                        <h3 className="text-sm font-medium text-gray-500">
                        CP LinkedIn Profile
                        </h3>
                        <a href={lead.CPLinkedInProfile} target="_blank" className="mt-1 text-blue-600 cursor-pointer hover:text-blue-600 hover:border-b-2 hover:border-b-blue-500">
                        {lead.CPLinkedInProfile || "-"}
                        </a>
                      </div>
                      {/* <div>
                        <h3 className="text-sm font-medium text-gray-500 flex justify-end">
                          Created At
                        </h3>
                        <p className="mt-1 text-gray-900">
                          {formatDate(lead.createdAt) || "-"}
                        </p>
                      </div> */}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column - Chat Box */}
          <div className="bg-white rounded-xl shadow-lg overflow-hidden flex flex-col h-[calc(100vh-12rem)]">
            <div className="px-6 py-4 bg-gradient-to-r from-blue-500 to-blue-600">
              <h1 className="text-2xl font-bold text-white">Messages</h1>
            </div>

            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {messages?.map((message, idx) => (
                <div
                  key={message._id}
                  className={`group flex ${
                    message.sender === "admin" ? "justify-end" : "justify-start"
                  }`}
                >
                  <div
                    className={`relative max-w-[70%] rounded-lg p-3 ${
                      message.sender === "admin"
                        ? "bg-blue-500 text-white"
                        : "bg-gray-100 text-gray-900"
                    }`}
                  >
                    <div className="flex justify-between items-center mb-1">
                      <div
                        className={`text-xs font-medium ${
                          message.sender === "admin"
                            ? "text-blue-100"
                            : "text-gray-600"
                        }`}
                      >
                        {message.userId?.name || "Unknown User"}
                      </div>
                      {/* Hover options */}
                      <div className="ml-2  opacity-0 group-hover:opacity-100 transition-opacity flex justify-end ">
                        <div className="flex gap-2 justify-end  ">
                          <button
                            type="button"
                            className="text-xs w-full  text-blue-700  underline"
                            onClick={() => handleEditMessage(idx)}
                          >
                            Edit
                          </button>
                          <button
                            type="button"
                            className="text-xs  text-red-500 hover:text-red-700 underline"
                            onClick={() => {handleDeleteMessage(idx)}}
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                    </div>
                  
                    <p
                      className={`text-sm mb-2 ${
                        message.success === "success"
                          ? "text-green-600"
                          : message.success === "error"
                          ? "text-red-600"
                          : "text-black"
                      }`}
                    >
                      {message.message}
                    </p>
                    {message.link && (
                      <div className="mt-2">
                        <img
                          src={message.link}
                          alt="Message attachment"
                          className="max-w-full h-auto rounded-md cursor-pointer hover:opacity-90 transition-opacity"
                          onClick={() => window.open(message.link, "_blank")}
                        />
                      </div>
                    )}
                    <p
                      className={`text-xs mt-1 ${
                        message.sender === "admin"
                          ? "text-blue-100"
                          : "text-gray-500"
                      }`}
                    >
                      {formatDate(message.createdAt)}
                    </p>
                  </div>
                </div>
              ))}
              <div ref={messagesEndRef} />
            </div>

            <form
              onSubmit={(e) =>
                editingMessageId
                  ? handleUpdateMessage(e)
                  : handleSendMessage(e)
              }
              className="p-4 border-t"
            >
              {imagePreview && (
                <div className="mb-2 relative">
                  <img
                    src={imagePreview}
                    alt="Preview"
                    className="max-h-32 rounded-md"
                  />
                  <button
                    type="button"
                    onClick={handleRemoveImage}
                    className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                  >
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </button>
                </div>
              )}
              <div className="flex space-x-2">
                <input
                  type="text"
                  value={editingMessageId ? editMessageText : newMessage}
                  onChange={(e) => {
                    if (editingMessageId) {
                      setEditMessageText(e.target.value);
                    } else {
                      setNewMessage(e.target.value);
                    }
                  }}
                  placeholder="Type your message..."
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleImageSelect}
                  accept="image/*"
                  className="hidden"
                  id="image-upload"
                />
                <label
                  htmlFor="image-upload"
                  className="px-4 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-500 cursor-pointer"
                >
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                    />
                  </svg>
                </label>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  Send
                </button>
                {editingMessageId && (
                  <button
                    type="button"
                    onClick={handleCancelEdit}
                    className="ml-2 px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400"
                  >
                    Cancel
                  </button>
                )}
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LeadDetail;
